import type { ReactNode } from "react";
import Animated from "react-native-reanimated";
import { type GetProps, isWeb } from "tamagui";
import type { ContainerLayoutProps } from "../../utils/layout";
import { webInteractiveTransition, webPointerProps } from "../interactive/styles";
import { type OptionalInteractiveProps, useInteractiveElement } from "../interactive/useInteractiveElement";
import { usePressScale } from "../interactive/usePressScale";
import { StyledCard } from "./StyledCard";
const AnimatedStyledCard = Animated.createAnimatedComponent(StyledCard);
export type CardVariant = "outlined" | "elevated";
export type CardSize = "small" | "large";

// Omit hook-managed props so consumers can't fight `useInteractiveElement`'s
// output. `role` / `aria-checked` / `aria-expanded` stay open — selection
// variants set them.
type StyledCardProps = Omit<GetProps<typeof StyledCard>, "interactive" | "tabIndex" | "onKeyDown" | "aria-disabled" | "onPressIn" | "onPressOut">;
type CommonCardProps = StyledCardProps & ContainerLayoutProps & {
  variant?: CardVariant;
  size?: CardSize;
  children?: ReactNode;
  /** Required only when visible content doesn't describe the action (WCAG 2.5.3). */
  "aria-label"?: string;
};
export type CardProps = CommonCardProps & OptionalInteractiveProps;
const cardInteractiveBehaviorStyles = {
  ...webPointerProps,
  ...webInteractiveTransition
} as const;
export const Card = ({
  kind,
  onPress,
  href,
  replace,
  scroll,
  shallow,
  disabled,
  ...rest
}: CardProps) => {
  const {
    isInteractive,
    renderTag,
    interactiveProps
  } = useInteractiveElement({
    kind,
    onPress,
    href,
    replace,
    scroll,
    shallow,
    disabled
  });
  const interactiveBehaviorStyles = !isInteractive || disabled ? {} : cardInteractiveBehaviorStyles;
  const {
    animatedStyle,
    onPressIn,
    onPressOut
  } = usePressScale(!isInteractive || disabled);
  return <AnimatedStyledCard
  // Re-key on render-tag transitions so Reanimated re-binds its native
  // ref when Tamagui swaps the underlying element (View ↔ button/a/span).
  // Without this, a Card whose `onPress` flips mid-life keeps a stale
  // animated handle and the press-scale worklet errors on native.
  key={renderTag ?? "static"} interactive={isInteractive} disabled={disabled} {...interactiveBehaviorStyles} {...renderTag ? {
    render: renderTag
  } : {}} {...interactiveProps} {...isInteractive && !disabled ? {
    onPressIn,
    onPressOut
  } : {}} style={isWeb ? renderTag === "a" ? {
    textDecoration: "none"
  } : undefined : animatedStyle} {...rest} data-sentry-element="AnimatedStyledCard" data-sentry-component="Card" data-sentry-source-file="Card.tsx" />;
};